import React, { useState, useRef, useEffect } from 'react';
import { Search, X, ChevronDown } from 'lucide-react';

const SearchableSelect = ({ 
  options = [], 
  value, 
  onChange, 
  placeholder = 'Search...', 
  labelKey = 'label', 
  valueKey = 'value',
  typeKey = null,
  filterTypes = [],
  testId = 'searchable-select'
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('All');
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedOption = options.find(o => o[valueKey] === value);
  const displayLabel = selectedOption ? (typeKey ? `${selectedOption[labelKey]} — ${selectedOption[typeKey]}` : selectedOption[labelKey]) : '';

  const filtered = options
    .filter(o => {
      if (query.length >= 2) {
        const label = (o[labelKey] || '').toLowerCase();
        const type = typeKey ? (o[typeKey] || '').toLowerCase() : '';
        const q = query.toLowerCase();
        if (!label.includes(q) && !type.includes(q)) return false;
      }
      if (activeFilter !== 'All' && typeKey) {
        return (o[typeKey] || '').toLowerCase() === activeFilter.toLowerCase();
      }
      return true;
    })
    .sort((a, b) => (a[labelKey] || '').localeCompare(b[labelKey] || ''));

  return (
    <div ref={containerRef} className="relative" data-testid={testId}>
      <div
        className="input-field flex items-center justify-between cursor-pointer"
        onClick={() => { setOpen(!open); setTimeout(() => inputRef.current?.focus(), 50); }}
      >
        <span className={`truncate ${value ? 'text-white' : 'text-[#A3B5C7]'}`}>
          {displayLabel || placeholder}
        </span>
        <ChevronDown className="w-4 h-4 text-[#A3B5C7] flex-shrink-0" />
      </div>

      {open && (
        <div className="absolute z-50 mt-1 w-full bg-[#0E1C33] border border-[#1A2F4C] rounded-lg shadow-xl max-h-80 flex flex-col">
          <div className="p-2 border-b border-[#1A2F4C]">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A3B5C7]" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Type to search..."
                className="w-full bg-[#0A1628] border border-[#1A2F4C] rounded px-8 py-1.5 text-sm text-white placeholder-[#A3B5C7] focus:outline-none focus:border-[#00D4C8]"
                data-testid={`${testId}-input`}
              />
              {query && (
                <button onClick={() => setQuery('')} className="absolute right-2.5 top-1/2 -translate-y-1/2">
                  <X className="w-3.5 h-3.5 text-[#A3B5C7]" />
                </button>
              )}
            </div>
          </div>

          {filterTypes.length > 0 && (
            <div className="flex flex-wrap gap-1 p-2 border-b border-[#1A2F4C]">
              {['All', ...filterTypes].map(ft => (
                <button
                  key={ft}
                  onClick={() => setActiveFilter(ft)}
                  className={`px-2 py-0.5 text-xs rounded transition-colors ${
                    activeFilter === ft
                      ? 'bg-[#00D4C8] text-[#0A1628]'
                      : 'bg-[#0A1628] text-[#A3B5C7] hover:text-white'
                  }`}
                >
                  {ft}
                </button>
              ))}
            </div>
          )}

          <div className="overflow-y-auto flex-1">
            {filtered.length === 0 ? (
              <p className="text-sm text-[#A3B5C7] p-3 text-center">No results</p>
            ) : (
              filtered.map(o => (
                <button
                  key={o[valueKey]}
                  onClick={() => { onChange(o[valueKey]); setOpen(false); setQuery(''); }}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-[#00D4C8]/10 transition-colors flex items-center justify-between ${
                    o[valueKey] === value ? 'bg-[#00D4C8]/5 text-[#00D4C8]' : 'text-white'
                  }`}
                >
                  <span className="truncate">{o[labelKey]}</span>
                  {typeKey && <span className="text-xs text-[#A3B5C7] ml-2 flex-shrink-0">{o[typeKey]}</span>}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
